<template>
  <div class="chat-toasts" aria-live="polite">
    <transition-group name="chat-toast">
      <div
        v-for="t in toasts"
        :key="t.key"
        class="chat-toast"
        @mouseenter="hold(t)"
        @mouseleave="release(t)"
      >
        <div class="d-flex align-items-start" style="gap: 10px">
          <span class="chat-toast-icon"><i class="fe fe-message-square"></i></span>
          <div style="min-width: 0; flex: 1">
            <div class="d-flex align-items-center">
              <strong class="text-truncate">{{ t.sender }}</strong>
              <button type="button" class="chat-toast-x ml-auto" title="Dismiss" @click="dismiss(t)">&times;</button>
            </div>
            <div v-if="t.subject" class="small font-weight-bold text-truncate">{{ t.subject }}</div>
            <div class="small text-muted chat-toast-preview">{{ t.preview }}</div>
            <div class="mt-2 d-flex" style="gap: 6px">
              <button v-if="t.tag" type="button" class="chat-toast-tag" :title="'Open ' + t.tag.label" @click="openRecord(t)">
                <i class="fe fe-file-text"></i>
              </button>
              <button type="button" class="btn chat-btn btn-sm px-3" @click="openIt(t)">Open chat</button>
              <button v-if="!t.tag" type="button" class="btn btn-outline-secondary btn-sm" @click="dismiss(t)">Dismiss</button>
            </div>
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import chat from "../store";
import { MAX_TOASTS, TOAST_MS } from "../config";
import { entityRoute } from "../entityRoutes";
import { openTag } from "../openTag";

// Bottom-right popup for new messages, shown on every page (mounted once in App.vue).
// Not shown for the conversation already open, on the login page, or while the tab is hidden.
export default {
  name: "ChatToast",
  data() {
    return { toasts: [] };
  },
  created() {
    chat.bus.$on("message", this.onMessage);
  },
  beforeDestroy() {
    chat.bus.$off("message", this.onMessage);
    this.toasts.forEach((t) => clearTimeout(t.timer));
  },
  methods: {
    onMessage(payload) {
      if (payload.mine || document.hidden || this.$route.path === "/") return;
      if (Number(chat.state.activeConversationId) === Number(payload.conversation_id)) return;

      // One card per conversation: a newer message replaces the older card.
      this.toasts.filter((t) => t.conversationId === payload.conversation_id).forEach(this.dismiss);

      const toast = {
        key: `${payload.conversation_id}-${payload.message_id}`,
        conversationId: payload.conversation_id,
        sender: (payload.sender && payload.sender.name) || "New message",
        subject: payload.subject,
        preview: payload.preview,
        // One tagged record that this app can link to -> offer it directly.
        tag: (payload.tags || []).length === 1 && entityRoute(payload.tags[0].type, payload.tags[0].id) ? payload.tags[0] : null,
        timer: null,
      };
      this.toasts.push(toast);
      while (this.toasts.length > MAX_TOASTS) this.dismiss(this.toasts[0]);
      this.release(toast);
    },
    hold(t) {
      clearTimeout(t.timer);
    },
    release(t) {
      clearTimeout(t.timer);
      t.timer = setTimeout(() => this.dismiss(t), TOAST_MS);
    },
    dismiss(t) {
      clearTimeout(t.timer);
      this.toasts = this.toasts.filter((x) => x !== t);
    },
    openRecord(t) {
      this.dismiss(t);
      openTag(this, t.tag);
    },
    openIt(t) {
      this.dismiss(t);
      this.$router.push(`/messages/${t.conversationId}`).catch(() => {});
    },
  },
};
</script>

<style scoped>
.chat-toasts {
  position: fixed;
  right: 16px;
  bottom: 80px; /* above the quick-message button (QuickChat.vue) */
  z-index: 1700; /* above page content, below the idle-timeout (2000) and pending-auth (1900) modals */
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 340px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}
.chat-toast {
  pointer-events: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #2563eb;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  padding: 12px 14px;
}
.chat-toast-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #eef2ff;
  color: #2563eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.chat-toast-preview {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.chat-toast-tag {
  width: 31px;
  height: 31px;
  border-radius: 50%;
  border: 0;
  background: #eef2ff;
  color: #2563eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.chat-toast-tag:hover {
  background: #dbe4ff;
}
.chat-toast-x {
  border: 0;
  background: transparent;
  color: #94a3b8;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0 0 0 6px;
}
.chat-toast-enter-active,
.chat-toast-leave-active {
  transition: all 0.2s ease;
}
.chat-toast-enter,
.chat-toast-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
